import React, { useRef, useCallback } from 'react';
import { WeddingProps, WeddingAssets, TextConfig } from '../../types/orders/wedding';
import { useARScene } from '../../hooks/useARScene';
import { useARCallbacks } from '../../hooks/useARCallbacks';
import { ARScene, ARImageTarget } from '../../components/ARScene';
import './WeddingPainting.css';

const SHARED_ASSETS: WeddingAssets = {
    frame: '/paintings/shared/images/frame.webp',
    colorFlowers: '/paintings/shared/images/color-flowers.webp',
    whiteFlowersBack: '/paintings/shared/images/white-flowers-back.webp',
    whiteFlowersTop: '/paintings/shared/images/white-flowers-top.webp',
};

const TEXT_CONFIG: TextConfig = {
    date: {
        position: [-0.35, -0.6, 0.004],
        fontSize: 0.06,
    },
    score: {
        position: [-0.52, -0.5, 0.005],
        fontSize: 0.065,
        rotation: [0, 0, 40],
    },
};

const calculateDaysFromNow = (date: string): number => {
    const startDate = new Date(date);
    const currentDate = new Date();
    const differenceInTime = currentDate.getTime() - startDate.getTime();
    return Math.floor(differenceInTime / (1000 * 60 * 60 * 24)) + 1;
};

const convertDateFormat = (date: string): string => {
    const [year, month, day] = date.split('-');
    return `${day}.${month}.${year}`;
};

export const WeddingPainting: React.FC<WeddingProps> = ({
    date,
    targetPath,
    picturePath,
}) => {
    const sceneRef = useRef<HTMLElement>(null);
    const { engine, ready, targetName, mindTargetUrl } = useARScene({
        targetJsonUrl: targetPath,
        sceneRef,
    });

    const updateTextInformation = useCallback(() => {
        if (!sceneRef.current) return;
        const textDate = sceneRef.current.querySelector('#text_date');
        const textScore = sceneRef.current.querySelector('#text_score');
        if (!textDate || !textScore) return;
        textDate.setAttribute('value', convertDateFormat(date));
        textScore.setAttribute('value', `${calculateDaysFromNow(date)} дней\nвместе`);
    }, [date]);

    useARCallbacks({
        sceneRef,
        targetName,
        engine,
        enabled: ready,
        callbacks: {
            targetFound: updateTextInformation,
        },
    });

    return (
        <div className="augmented_layout">
            <ARScene
                engine={engine}
                ready={ready}
                mindTargetUrl={mindTargetUrl}
                sceneRef={sceneRef}
            >
                <a-assets>
                    <img
                        id="resource_picture"
                        src={picturePath}
                        alt='Resource art'
                    />
                    <img
                        id="resource_frame"
                        src={SHARED_ASSETS.frame}
                        alt='Resource frame'
                    />
                    <img
                        id="resource_white_flowers_back"
                        src={SHARED_ASSETS.whiteFlowersBack}
                        alt='Resource back white flowers'
                    />
                    <img
                        id="resource_white_flowers_top"
                        src={SHARED_ASSETS.whiteFlowersTop}
                        alt='Resource top white flowers'
                    />
                    <img
                        id="resource_color_flowers"
                        src={SHARED_ASSETS.colorFlowers}
                        alt='Resource color flowers'
                    />
                    <a-asset-item
                        id="adleryFont"
                        src="/paintings/shared/fonts/adlery.woff"
                    />
                </a-assets>

                <a-camera
                    position="0 0 0"
                    look-controls="enabled: false"
                    cursor="fuse: false; rayOrigin: mouse;"
                    raycaster="far: 1000; objects: .clickable"
                />

                <ARImageTarget engine={engine} targetName={targetName}>
                    <a-image
                        id="image_picture"
                        src="#resource_picture"
                        width="1.1"
                        height="1.65"
                        position="0 0 -0.001"
                    />

                    <a-image
                        id="image_white_flowers_back"
                        src="#resource_white_flowers_back"
                        width="1.54"
                        height="1.94"
                        position="-0.05 0.1 0"
                    />

                    <a-image
                        id="image_frame"
                        src="#resource_frame"
                        width="1.1"
                        height="1.65"
                        position="0 0 0.001"
                    />

                    <a-image
                        id="image_white_flowers_top"
                        src="#resource_white_flowers_top"
                        width="1.46"
                        height="1.57"
                        position="-0.05 0 0.002"
                    />

                    <a-image
                        id="image_color_flowers"
                        src="#resource_color_flowers"
                        width="1.4"
                        height="0.77"
                        position="0.05 0 0.003"
                    />

                    <a-troika-text
                        id="text_date"
                        value=""
                        color="#EF8818"
                        position={TEXT_CONFIG.date.position.join(' ')}
                        font-size={TEXT_CONFIG.date.fontSize}
                        baseline="top"
                        align="center"
                        font="/paintings/shared/fonts/adlery.woff"
                    />

                    <a-troika-text
                        id="text_score"
                        value=""
                        color="#EF8818"
                        position={TEXT_CONFIG.score.position.join(' ')}
                        font-size={TEXT_CONFIG.score.fontSize}
                        line-height="1.2"
                        rotation={TEXT_CONFIG.score.rotation.join(' ')}
                        baseline="top"
                        align="center"
                        font="/paintings/shared/fonts/adlery.woff"
                    />
                </ARImageTarget>
            </ARScene>
        </div>
    );
};
